/**
 * pages/knowledge/register.js — МИНУТЫН МЭДЛЭГ (м-мэдлэг)
 *
 * Эх сурвалж: «process flow HSD-2026.vsdx» → "м-мэдлэг" таб
 *   (МИНУТЫН МЭДЛЭГ ОЛГОХ ПРОЦЕСС).
 *
 * Нэг хуудас, эрхээр нь табууд:
 *   • Өнөөдрийн мэдлэг  — БҮХ ажилтан: 7 хоногийн сэдвийн бичлэгийг үзэж,
 *                          өдрийн 1 асуултад хариулна.
 *   • Мэдлэгийн сан     — ЗӨВХӨН ХАБЭА хэлтэс: сэдэв, бичлэг, асуулт бүртгэх.
 *   • 7 хоногийн хуваарь — ЗӨВХӨН ХАБЭА хэлтэс: аль долоо хоногт аль сэдэв.
 *
 * Биелэлтийн хяналт нь тусдаа хуудсанд: /knowledge/dashboard
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MonthTag from "src/components/knowledge/MonthTag";
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Alert,
  Tooltip,
  message,
} from "antd";
import { useSearchParams } from "react-router-dom";
import { useUserContext } from "src/contexts/userContext";
import { knowledgeApi } from "src/api/knowledgeApi";
import { KNOWLEDGE, KNOWLEDGE_DARK, KNOWLEDGE_LIGHT } from "src/pages/knowledge/theme";
// Файлын хэмжээний нэгдсэн хамгаалалт — 16 хуудсад хуваалцана.
import { guardFile, showUploadWarning } from "src/components/MediaUpload";
import PageHelp from "src/components/help/PageHelp";

const { TextArea } = Input;

// Модулийн өнгө — цайвар цэнхэр (theme.js)
const PRIMARY = KNOWLEDGE_DARK;
const PRIMARY_SOFT = KNOWLEDGE_LIGHT;

// Одоогийн сар (1-12) — 365 хоногийн микро сургалтын бүлэг сараар
// зохион байгуулагддаг тул «энэ сарын бүлэг» нь анхны сонголт болно.
const CURRENT_MONTH = new Date().getMonth() + 1;

/**
 * Бүлгийн нэрийг ЦЭВЭР харуулна — «8-р сар: Авто тээвэр…» → «Авто тээвэр…».
 *
 * ⚠ ЯАГААД ХЭРЭГТЭЙ ВЭ: сар нь одоо ТУСДАА багана (`month_no`) бөгөөд
 *   дэлгэц дээр шошгоор харагддаг. Гэвч анхны миграц нь сарыг НЭРЭНД нь ч
 *   бичсэн байсан тул «8-р сар · 8-р сар: Авто тээвэр…» гэж ДАВХАРДАЖ,
 *   шошго хэт уртсаад таб/сонголтоос халин гарч байв.
 *
 * ⚠ Зөвхөн `month_no` тавигдсан үед л тайрна. Сартай холбоогүй бүлгийн
 *   нэрэнд санамсаргүй «1-р сар:» гэсэн текст байвал түүнийг хөндөхгүй.
 */
const MONTH_PREFIX = /^\s*\d{1,2}\s*-?\s*р\s+сар\s*[:：]\s*/i;
const groupLabel = (g) => {
  const name = String(g?.name || "");
  return g?.month_no ? name.replace(MONTH_PREFIX, "") : name;
};

const MANAGE_ROLES = [
  "sso_owner",
  "hab_dept_specialist",
  "hab_meregjilten",
  "hse_specialist",
];
const MANAGE_GROUPS = ["173", "174"];

const DAY_NAMES = ["Даваа", "Мягмар", "Лхагва", "Пүрэв", "Баасан", "Бямба", "Ням"];
const OPTION_KEYS = ["A", "B", "C", "D"];

const CONTENT_TYPES = [
  { value: "video", label: "Бичлэг (видео)" },
  { value: "poster", label: "Постер / зураг" },
  { value: "infographic", label: "Инфографик" },
];

// ===========================================================================
//  Өнөөдрийн мэдлэг — ажилтны дэлгэц
//  (ажилтны хувийн хуудас /knowledge/my дээр мөн ашиглагдана)
// ===========================================================================
export function TodayPanel({ refreshKey }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  // Групп хайх — 365 хоногийн микро сургалт нэвтэрснээр групп 12+ болж,
  // табын мөр дүүрсэн тул нэрээр нь шүүж олдог болгов.
  const [todayQuery, setTodayQuery] = useState("");
  // Идэвхтэй таб — УДИРДДАГ (controlled). `defaultActiveKey` нь зөвхөн
  // ПЕРВЫЙ рендэрт ажилладаг тул шүүлт/дахин татахад буруу табд үлдэнэ.
  const [activeKey, setActiveKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await knowledgeApi.today());
    } catch (e) {
      message.error(
        e?.response?.data?.message || "Өнөөдрийн мэдлэгийг татаж чадсангүй."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // refreshKey өөрчлөгдөх бүрд дахин татна — таб руу буцаж ирэхэд, эсвэл
  // админ талд бичлэг/асуулт/7 хоногийн хуваарь хадгалагдсаны дараа.
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const allItems = useMemo(() => data?.items || [], [data]);

  /** Хайлтаар шүүсэн группууд. Хоосон үр дүнд БҮГДИЙГ буцаана — эс бөгөөс
   *  таб огт үлдэхгүй, хэрэглэгч «мэдлэг алга» гэж эндүүрнэ. */
  const shownItems = useMemo(() => {
    const q = todayQuery.trim().toLowerCase();
    if (!q) return allItems;
    const f = allItems.filter((it) =>
      String(it.group?.name || "").toLowerCase().includes(q),
    );
    return f.length ? f : allItems;
  }, [allItems, todayQuery]);

  /**
   * ⚠ Анхны таб нь ЭНЭ САРЫН групп.
   *   Өмнө нь `defaultActiveKey={items[0]}` байсан тул 365 хоногийн микро
   *   сургалт нэвтэрснээр ҮРГЭЛЖ 1-р сарын бүлэг нээгддэг байв — ажилтан
   *   9-р сард 1-р сарын сэдэв хараад эхэлнэ.
   *   Энэ сарын бүлэг байхгүй бол эхнийхийг авна (хуучин зан төлөв).
   */
  useEffect(() => {
    if (!shownItems.length) return;
    const exists = shownItems.some(
      (it) => String(it.group?.id) === String(activeKey),
    );
    if (exists) return;
    const cur = shownItems.find((it) => it.group?.month_no === CURRENT_MONTH);
    setActiveKey(String((cur || shownItems[0]).group?.id ?? "0"));
  }, [shownItems, activeKey]);

  // Эхний ачаалалт дээр л том Spin — дахин татахад (үзэлт тэмдэглэх, шинэчлэх)
  // дэлгэц бүхэлдээ анивчихгүй, хэрэглэгч байрандаа үлдэнэ.
  if (loading && !data) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  const items = allItems;
  if (!items.length) {
    return (
      <Empty
        description={
          data?.message || "Танд нээлттэй мэдлэгийн групп алга байна."
        }
        style={{ padding: 60 }}
      />
    );
  }

  const header = (
    <div
      style={{
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span style={{ fontSize: 12, color: "#8A94A6", fontWeight: 700 }}>
        {data.year_no} ОНЫ {data.week_no}-Р ДОЛОО ХОНОГ ·{" "}
        {DAY_NAMES[(data.day_no || 1) - 1]} ГАРАГ
      </span>
      <Space wrap>
        <span style={{ fontSize: 12 }}>
          Энэ 7 хоногт:{" "}
          <b style={{ color: PRIMARY }}>{data.total_points_this_week || 0}</b>{" "}
          Safety Point
        </span>
        {/* Групп 4-өөс олон бол л хайлт хэрэгтэй — цөөхөн үед зай эзэлнэ */}
        {allItems.length > 4 ? (
          <Input
            allowClear
            size="small"
            placeholder="Бүлэг хайх"
            prefix={<i className="fas fa-search" style={{ color: "#B0B7C3" }} />}
            style={{ width: 190 }}
            value={todayQuery}
            onChange={(e) => setTodayQuery(e.target.value)}
          />
        ) : null}
        <Button size="small" onClick={load} loading={loading}>
          <i className="fas fa-sync" style={{ marginRight: 6 }} />
          Шинэчлэх
        </Button>
      </Space>
    </div>
  );

  /**
   * ⚠ ТАБ БИШ, СОНГОГЧ.
   *   Урьд нь групп бүрд нэг таб байсан. 365 хоногийн микро сургалт
   *   нэвтэрснээр групп 12+ болж, нэр нь урт («8-р сар: Авто тээвэр ба
   *   Уурхайн тээвэр») тул табын мөр хэвтээгээр халин гарч, сүүлийн
   *   бүлгүүд гүйлгэлтийн ард нуугдаж байв. Таб нь 3-5 зүйлд тохирдог.
   *
   *   Сонгогч нь: хэдэн ч бүлгийг багтаана · хайлттай · шошго бүр дээр
   *   өнгө, сар, дуусгасан эсэх нь харагдана.
   */
  const curKey = activeKey ?? String(shownItems[0]?.group?.id ?? "0");
  const activeItem =
    shownItems.find((it) => String(it.group?.id) === String(curKey)) ||
    shownItems[0];

  return (
    <>
      {header}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>Бүлэг:</span>
        <Select
          showSearch
          value={curKey}
          onChange={setActiveKey}
          // ⚠ Хайлт нь ЦЭВЭР ТЕКСТЭЭР (`optionFilterProp="label"`) —
          //   сонголтын агуулга нь JSX тул түүгээр хайж болохгүй.
          optionFilterProp="label"
          style={{ minWidth: 300, maxWidth: 520, flex: 1 }}
          dropdownMatchSelectWidth={false}
        >
          {shownItems.map((item, idx) => {
            const g = item.group || {};
            const done = !!item.my_view?.is_completed && !!item.my_answer;
            return (
              <Select.Option
                key={String(g.id ?? idx)}
                value={String(g.id ?? idx)}
                label={`${g.month_no ? `${g.month_no}-р сар ` : ""}${groupLabel(g)}`}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 9,
                      height: 9,
                      borderRadius: 5,
                      background: g.color_hex || PRIMARY,
                      flexShrink: 0,
                    }}
                  />
                  {g.month_no ? (
                    <Tag
                      color={g.month_no === CURRENT_MONTH ? "gold" : "default"}
                      style={{ marginRight: 0 }}
                    >
                      {g.month_no}-р сар
                    </Tag>
                  ) : null}
                  <span style={{ flex: 1 }}>{groupLabel(g)}</span>
                  <Tag color={done ? "green" : "gold"} style={{ marginRight: 0 }}>
                    {done ? "✓" : "!"}
                  </Tag>
                </span>
              </Select.Option>
            );
          })}
        </Select>
        <span style={{ fontSize: 12, color: "#8A94A6" }}>
          {shownItems.length} бүлэг
        </span>
      </div>

      {activeItem ? (
        <GroupKnowledge
          // ⚠ `key` — бүлэг солиход GroupKnowledge-ийн дотоод төлөв
          //   (сонгосон хариулт, бичлэг үзсэн тэмдэглэл) ШИНЭЧЛЭГДЭНЭ.
          //   Эс бөгөөс өмнөх бүлгийн хариулт шинэ бүлэгт үлдэнэ.
          key={curKey}
          item={activeItem}
          onChanged={load}
        />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ *
 *  Нэг группын өнөөдрийн мэдлэг — бичлэг + асуулт + 7 хоногийн явц
 * ------------------------------------------------------------------ */
function GroupKnowledge({ item, onChanged }) {
  const [data, setData] = useState(item);
  const [selected, setSelected] = useState(null);
  const [sending, setSending] = useState(false);
  const videoRef = useRef(null);
  const viewSentRef = useRef(!!item?.my_view?.is_completed);

  useEffect(() => {
    setData(item);
    viewSentRef.current = !!item?.my_view?.is_completed;
    setSelected(null);
  }, [item]);

  const content = data?.content;
  const question = data?.question;
  const myAnswer = data?.my_answer;
  const viewed = !!data?.my_view?.is_completed;

  // Видеог үзэж дуусахад (эсвэл постер нээхэд) үзэлтийг бүртгэнэ
  const markViewed = useCallback(
    async (watchedSeconds) => {
      if (!content || viewSentRef.current) return;
      viewSentRef.current = true;
      try {
        await knowledgeApi.markView({
          content_id: content.id,
          watched_seconds: Math.round(watchedSeconds || 0),
          completed: true,
        });
        setData((d) =>
          d ? { ...d, my_view: { ...(d.my_view || {}), is_completed: true } } : d
        );
        message.success("Танилцсан тэмдэглэгдлээ.");
        onChanged && onChanged();
      } catch (e) {
        viewSentRef.current = false;
        message.error(e?.response?.data?.message || "Бүртгэж чадсангүй.");
      }
    },
    [content, onChanged]
  );

  const onSubmitAnswer = async () => {
    if (!question || !selected) {
      message.warning("Хариултаа сонгоно уу.");
      return;
    }
    setSending(true);
    try {
      const res = await knowledgeApi.answer({
        question_id: question.id,
        answer_option: selected,
      });
      setData((d) => (d ? { ...d, my_answer: res } : d));
      if (res.already_answered) {
        message.info("Өнөөдрийн асуултад аль хэдийн хариулсан байна.");
      } else if (res.is_correct) {
        message.success(`Зөв! +${res.points_awarded} Safety Point`);
      } else {
        message.warning("Буруу хариулт. Тайлбарыг уншина уу.");
      }
    } catch (e) {
      message.error(e?.response?.data?.message || "Хариулт илгээж чадсангүй.");
    } finally {
      setSending(false);
    }
  };

  if (!data?.has_content) {
    return (
      <Empty
        description={
          data?.message || "Энэ долоо хоногт сэдэв нээгдээгүй байна."
        }
        style={{ padding: 40 }}
      />
    );
  }

  const progress = data.week_progress || [];
  const doneDays = progress.filter((p) => p.viewed).length;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={15}>
        <Card
          bodyStyle={{ padding: 16 }}
          title={
            <Space direction="vertical" size={0}>
              <span
                style={{
                  fontSize: 12,
                  color: "#8A94A6",
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  flexWrap: "wrap",
                }}
              >
                {/* ⚠ Уншиж буй сэдэв аль САРЫНХ болох нь толгойд гарна —
                    хуучин сарын сэдэв уншиж байгаагаа мэдэхгүй бол микро
                    сургалтын дараалал алдагдана. */}
                <MonthTag month={data.group?.month_no} />
                {data.group?.name || "Минутын мэдлэг"}
                {data.week?.week_no ? ` · ${data.week.week_no}-р долоо хоног` : ""}
              </span>
              <span style={{ fontSize: 17, fontWeight: 800, color: PRIMARY }}>
                {data.topic?.name}
              </span>
            </Space>
          }
          extra={
            data.topic?.category_name ? (
              <Tag color={data.topic.category_code === "EA" ? "blue" : "green"}>
                {data.topic.category_name}
              </Tag>
            ) : null
          }
        >
          {!content ? (
            <Empty description="Энэ өдөрт бичлэг оруулаагүй байна." />
          ) : content.content_type === "video" && content.file_url ? (
            // Тоглуулагчийг хязгаарлана: өргөн дэлгэц дээр бичлэг хэт том
            // болж, асуулт нь доош түлхэгдэн скролл шаарддаг байв. Бичлэг ба
            // асуулт НЭГ дэлгэцэнд багтах нь минутын мэдлэгийн зорилгод нийцнэ.
            <video
              ref={videoRef}
              src={content.file_url}
              controls
              controlsList="nodownload"
              style={{
                width: "100%",
                maxWidth: 720,
                maxHeight: "52vh",
                display: "block",
                margin: "0 auto",
                objectFit: "contain",
                borderRadius: 10,
                background: "#000",
              }}
              onEnded={(e) => markViewed(e.target.currentTime)}
            />
          ) : content.file_url ? (
            <img
              src={content.file_url}
              alt={content.title}
              style={{
                width: "100%",
                maxWidth: 720,
                maxHeight: "52vh",
                display: "block",
                margin: "0 auto",
                objectFit: "contain",
                borderRadius: 10,
              }}
              onLoad={() => markViewed(0)}
            />

          ) : (
            <Empty description="Файл байхгүй байна." />
          )}

          {content ? (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
                {content.title}
              </p>
              {content.description ? (
                <p style={{ color: "#5F5E5A", marginBottom: 8 }}>
                  {content.description}
                </p>
              ) : null}
              <Space>
                {viewed ? (
                  <Tag color="green">
                    <i className="fas fa-check" /> Танилцсан
                  </Tag>
                ) : (
                  <Button
                    type="primary"
                    style={{ background: PRIMARY, borderColor: PRIMARY }}
                    onClick={() =>
                      markViewed(videoRef.current?.currentTime || 0)
                    }
                  >
                    Танилцсан гэж тэмдэглэх
                  </Button>
                )}
                {content.duration_sec ? (
                  <span style={{ color: "#8A94A6", fontSize: 12 }}>
                    {content.duration_sec} сек
                  </span>
                ) : null}
              </Space>
            </div>
          ) : null}
        </Card>
      </Col>

      <Col xs={24} lg={9}>
        {/* Өдрийн 1 асуулт */}
        <Card
          title={<span style={{ fontWeight: 800 }}>Өдрийн асуулт</span>}
          bodyStyle={{ padding: 16 }}
          style={{ marginBottom: 16 }}
        >
          {!question ? (
            <Empty description="Энэ өдөрт асуулт оруулаагүй байна." />
          ) : (
            <>
              <p style={{ fontWeight: 700, marginBottom: 12 }}>
                {question.question_text}
              </p>
              <Radio.Group
                value={myAnswer?.answer_option || selected}
                disabled={!!myAnswer}
                onChange={(e) => setSelected(e.target.value)}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                {OPTION_KEYS.map((k) => {
                  const text = question[`option_${k.toLowerCase()}`];
                  if (!text) return null;
                  const isCorrect =
                    myAnswer && myAnswer.correct_option === k;
                  const isWrongPick =
                    myAnswer && myAnswer.answer_option === k && !myAnswer.is_correct;
                  return (
                    <Radio
                      key={k}
                      value={k}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "1px solid #E6E9EF",
                        background: isCorrect
                          ? "#E8F7EF"
                          : isWrongPick
                          ? "#FDECEC"
                          : "#fff",
                      }}
                    >
                      <b>{k}.</b> {text}
                    </Radio>
                  );
                })}
              </Radio.Group>

              {myAnswer ? (
                <div style={{ marginTop: 12 }}>
                  <Tag color={myAnswer.is_correct ? "green" : "red"}>
                    {myAnswer.is_correct
                      ? `Зөв · +${myAnswer.points_awarded} оноо`
                      : "Буруу"}
                  </Tag>
                  {myAnswer.explanation ? (
                    <p style={{ marginTop: 8, color: "#5F5E5A" }}>
                      {myAnswer.explanation}
                    </p>
                  ) : null}
                </div>
              ) : (
                <Button
                  type="primary"
                  block
                  loading={sending}
                  style={{
                    marginTop: 12,
                    background: PRIMARY,
                    borderColor: PRIMARY,
                  }}
                  onClick={onSubmitAnswer}
                >
                  Хариулт илгээх
                </Button>
              )}
            </>
          )}
        </Card>

        {/* Долоо хоногийн биелэлт */}
        <Card
          title={<span style={{ fontWeight: 800 }}>Энэ долоо хоног</span>}
          bodyStyle={{ padding: 16 }}
        >
          <Progress
            percent={Math.round((doneDays / 7) * 100)}
            strokeColor={PRIMARY}
            format={() => `${doneDays}/7`}
          />
          <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
            {progress.map((p) => (
              <Tooltip
                key={p.date}
                title={`${DAY_NAMES[p.day_no - 1]} · ${p.date}${
                  p.answered ? (p.is_correct ? " · зөв" : " · буруу") : ""
                }`}
              >
                <div
                  style={{
                    flex: 1,
                    textAlign: "center",
                    padding: "8px 0",
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 700,
                    color: p.viewed ? "#fff" : "#8A94A6",
                    background: p.viewed
                      ? PRIMARY
                      : p.is_today
                      ? PRIMARY_SOFT
                      : "#F3F4F6",
                    border: p.is_today ? `2px solid ${PRIMARY}` : "none",
                    opacity: p.is_future ? 0.5 : 1,
                  }}
                >
                  {DAY_NAMES[p.day_no - 1].slice(0, 2)}
                  <div style={{ fontSize: 10 }}>
                    {p.answered ? (p.is_correct ? "✓" : "✕") : "·"}
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>
          <p style={{ marginTop: 12, marginBottom: 0, fontSize: 12, color: "#5F5E5A" }}>
            Энэ 7 хоногт цуглуулсан:{" "}
            <b style={{ color: PRIMARY }}>{data.points_this_week || 0}</b>{" "}
            Safety Point
          </p>
        </Card>
      </Col>
    </Row>
  );
}

// ===========================================================================
//  Мэдлэгийн сан — сэдэв / бичлэг / асуулт (ХАБЭА хэлтэс)
// ===========================================================================
function TopicPanel({ onDataChanged }) {
  const [loading, setLoading] = useState(true);
  const [topics, setTopics] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  /**
   * Группын шүүлт нь URL-аас эхэлж болно (`?group=<id>`).
   *
   * Лавлах хуудсан дээр группын сэдвийн тоог дарахад ТУХАЙН группын
   * сэдвүүд шууд шүүгдсэн байдлаар нээгдэнэ — 365 сэдэвтэй санд орж ирээд
   * группээ дахин сонгох шаардлагагүй.
   */
  const [tpSearchParams] = useSearchParams();
  const [groupFilter, setGroupFilter] = useState(() => {
    const g = Number(tpSearchParams.get("group"));
    return Number.isFinite(g) && g > 0 ? g : null;
  });
  // Текст хайлт ба ангиллын шүүлт — группээр шүүгээд ч 30+ мөр үлддэг.
  const [topicQuery, setTopicQuery] = useState("");
  const [topicCatFilter, setTopicCatFilter] = useState(null);
  // Хуудсанд харуулах мөрийн тоог хэрэглэгч сонгоно
  const [pageSize, setPageSize] = useState(20);
  const [topicModal, setTopicModal] = useState(null); // null | {} | topic
  const [detail, setDetail] = useState(null); // сонгосон сэдвийн дэлгэрэнгүй
  const [topicForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await knowledgeApi.topics();
      setTopics(res.topics || []);
      setCategories(res.categories || []);
      setGroups(res.groups || []);
    } catch (e) {
      message.error(e?.response?.data?.message || "Сэдвүүдийг татаж чадсангүй.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * ЭНЭ САРЫН бүлгийг анхны утгаар сонгоно.
   *
   * 365 хоногийн микро сургалтад ажилтан өдөр бүр ЭНЭ САРЫН сэдвээр
   * суралцдаг тул ХАБЭА хэлтэс ч ихэвчлэн энэ сарын сэдвүүдийг засна.
   * Бүх 365 сэдвийг харуулж эхлэх нь ашиггүй — хэрэглэгч бараг үргэлж
   * группээ эхлээд сонгодог байв.
   *
   * ⚠ ЗӨВХӨН НЭГ УДАА тавина (`defaultAppliedRef`). Эс бөгөөс хэрэглэгч
   *   шүүлтээ цэвэрлэх (бүх группийг харах) бүрд энэ сарынх нь дахин
   *   эргэж тавигдаж, «цэвэрлэж чадахгүй» болно.
   * ⚠ URL-аас групп заасан бол (`?group=`) түүнийг ДАРЖ БИЧИХГҮЙ —
   *   лавлахаас тодорхой группын сэдэв рүү орж ирсэн хүн өөр группд
   *   хаягдах ёсгүй.
   */
  const defaultAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultAppliedRef.current) return;
    if (!groups.length) return;
    defaultAppliedRef.current = true;
    if (tpSearchParams.get("group")) return; // URL-ынх давуу
    const cur = groups.find((g) => g.month_no === CURRENT_MONTH);
    if (cur) setGroupFilter(cur.id);
  }, [groups, tpSearchParams]);

  const openTopic = async (row) => {
    try {
      setDetail(await knowledgeApi.topic(row.id));
    } catch (e) {
      message.error("Дэлгэрэнгүйг татаж чадсангүй.");
    }
  };

  const saveTopic = async () => {
    try {
      const values = await topicForm.validateFields();
      if (topicModal?.id) {
        await knowledgeApi.updateTopic(topicModal.id, values);
      } else {
        await knowledgeApi.createTopic(values);
      }
      message.success("Хадгаллаа.");
      setTopicModal(null);
      load();
      onDataChanged && onDataChanged();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || "Хадгалж чадсангүй.");
    }
  };

  /**
   * Харагдах сэдвүүд — групп, ангилал, текст гурван шүүлтийг хамтад нь.
   * Хайлт нь КОД ба НЭР хоёуланг хардаг: «MICRO-M09-07» гэж ч,
   * «Хийн хэмжилт» гэж ч олдоно.
   */
  const shownTopics = useMemo(() => {
    const q = topicQuery.trim().toLowerCase();
    return (topics || []).filter((t) => {
      if (groupFilter && t.group_id !== groupFilter) return false;
      if (topicCatFilter && t.category_code !== topicCatFilter) return false;
      if (!q) return true;
      return (
        String(t.code || "").toLowerCase().includes(q) ||
        String(t.name || "").toLowerCase().includes(q)
      );
    });
  }, [topics, groupFilter, topicCatFilter, topicQuery]);

  const columns = [
    {
      title: "№",
      width: 44,
      align: "center",
      render: (_v, _r, i) => (
        <span style={{ fontSize: 10.5, color: "#9AA3B0" }}>{i + 1}</span>
      ),
    },
    {
      // ⚠ Код нь УНШИХ зүйл биш, ТАНИХ тэмдэг (MICRO-M09-07). Фонтыг
      //   жижигрүүлж monospace болгосноор нүд нь гол агуулга руу (сэдвийн
      //   нэр) шууд очно — /knowledge/reference-тэй ИЖИЛ зарчим.
      // ⚠ Фонт жижгэрсэн ч багана ӨРГӨН: микро сургалтын код 12 тэмдэгт
      //   (MICRO-M09-07) тул 90px-д багтахгүй, хоёр мөр болж таслагдаж байв.
      title: "Код",
      dataIndex: "code",
      width: 128,
      render: (v) => (
        <span
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 11,
            color: "#6B7280",
            whiteSpace: "nowrap",
            letterSpacing: 0.2,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: "Сэдэв",
      dataIndex: "name",
      onCell: () => ({ style: { whiteSpace: "normal" } }),
      // Дарж болохыг ТОДОРХОЙ болгов: өмнө нь энгийн `<a>` байсан тул
      // хэрэглэгч дарж дэлгэрэнгүй (бичлэг/асуулт) нээгддэгийг мэддэггүй байв.
      render: (v, r) => (
        <Tooltip title="Дарж бичлэг, асуултыг нь харах / нэмэх">
          {/* `<a>` биш `<button>` — href-гүй холбоос нь гараас хандах
              боломжгүй (jsx-a11y/anchor-is-valid) */}
          <Button
            type="link"
            onClick={() => openTopic(r)}
            style={{
              padding: 0,
              height: "auto",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              textAlign: "left",
              whiteSpace: "normal",
            }}
          >
            <i className="fas fa-folder-open" style={{ fontSize: 11 }} />
            {v}
            <i
              className="fas fa-chevron-right"
              style={{ fontSize: 9, opacity: 0.55 }}
            />
          </Button>
        </Tooltip>
      ),
    },
    {
      title: "Групп",
      dataIndex: "group_name",
      width: 190,
      render: (v, r) =>
        v ? (
          <Space>
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: 5,
                background: r.group_color || PRIMARY,
                display: "inline-block",
              }}
            />
            {v}
          </Space>
        ) : (
          <Tag color="red">Группгүй</Tag>
        ),
    },
    {
      title: "Ангилал",
      dataIndex: "category_code",
      width: 150,
      render: (v, r) => (
        <Tag color={v === "EA" ? "blue" : "green"}>
          {r.category_name || (v === "EA" ? "Эрүүл ахуй" : "Ажлын аюулгүй байдал")}
        </Tag>
      ),
    },
    {
      title: "Бичлэг",
      dataIndex: "content_count",
      width: 90,
      align: "center",
      render: (v) => <Tag color={v ? "green" : "default"}>{v || 0}</Tag>,
    },
    {
      title: "Асуулт",
      dataIndex: "question_count",
      width: 90,
      align: "center",
      render: (v) => <Tag color={v ? "blue" : "default"}>{v || 0}</Tag>,
    },
    {
      // Хэн, хэзээ бүртгэсэн — лавлахын хариуцлагыг мөрөөс нь шууд харна
      title: "Бүртгэсэн",
      width: 140,
      onCell: () => ({ style: { whiteSpace: "normal" } }),
      render: (_v, r) =>
        r.insert_user_name || r.insert_date ? (
          <div style={{ lineHeight: 1.35 }}>
            <div style={{ fontSize: 11.5 }}>{r.insert_user_name || "—"}</div>
            {r.insert_date ? (
              <div style={{ fontSize: 10, color: "#9AA3B0" }}>
                {r.insert_date}
              </div>
            ) : null}
          </div>
        ) : (
          <span style={{ color: "#C4C9D1" }}>—</span>
        ),
    },
    {
      // ⚠ Icon товч бүр ЗААВАЛ Tooltip-тэй — icon дангаараа «энэ юу хийдэг
      //   вэ» гэдгийг хэлдэггүй тул текстийг зүгээр icon-оор солих нь
      //   хэрэглэгчийг таамаглуулна.
      title: "Үйлдэл",
      width: 96,
      align: "center",
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Засах">
            <Button
              size="small"
              type="text"
              icon={<i className="fas fa-pen" />}
              onClick={() => {
                setTopicModal(r);
                topicForm.setFieldsValue(r);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Устгах уу?"
            description="Сэдвийн бичлэг, асуултууд ч хамт харагдахаа болино."
            okText="Устгах"
            cancelText="Болих"
            onConfirm={async () => {
              await knowledgeApi.deleteTopic(r.id);
              message.success("Устгалаа.");
              load();
            }}
          >
            <Tooltip title="Устгах">
              <Button
                size="small"
                type="text"
                danger
                icon={<i className="fas fa-trash" />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <Space wrap>
          {/* ── ХАЙЛТ ────────────────────────────────────────────────
              365 хоногийн микро сургалт нэвтэрснээр сэдэв 365+ болсон тул
              группээр шүүгээд ч 30 гаруй мөр үлддэг — нүдээр хайх
              боломжгүй. Хайлт нь КЛИЕНТ талд: сэдвүүд бүгд нэг хүсэлтээр
              ирдэг тул сервер рүү дахин очих шаардлагагүй. */}
          <Input
            allowClear
            placeholder="Код, сэдвээр хайх"
            prefix={<i className="fas fa-search" style={{ color: "#B0B7C3" }} />}
            style={{ width: 240 }}
            value={topicQuery}
            onChange={(e) => setTopicQuery(e.target.value)}
          />
          <span style={{ fontWeight: 700 }}>Групп:</span>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 240 }}
            placeholder="Бүх групп"
            value={groupFilter}
            onChange={setGroupFilter}
            // Энэ сарын бүлгийг ТЭМДЭГЛЭНЭ — анхны утгаар сонгогдсон
            // байдаг тул «яагаад энэ групп сонгогдчихов» гэсэн эргэлзээ
            // төрөхгүй.
            options={groups.map((g) => ({
              value: g.id,
              label:
                g.month_no === CURRENT_MONTH
                ? `${groupLabel(g)} — энэ сар`
                : groupLabel(g),
            }))}
          />
          <Select
            allowClear
            placeholder="Ангилал"
            style={{ width: 170 }}
            value={topicCatFilter}
            onChange={setTopicCatFilter}
            options={(categories.length
              ? categories
              : [
                  { code: "AA", name: "Ажлын аюулгүй байдал" },
                  { code: "EA", name: "Эрүүл ахуй, эрүүл мэнд" },
                ]
            ).map((c) => ({ value: c.code, label: c.name }))}
          />
        </Space>
        <Button
          type="primary"
          style={{ background: PRIMARY, borderColor: PRIMARY }}
          onClick={() => {
            setTopicModal({});
            topicForm.resetFields();
            topicForm.setFieldsValue({
              category_code: "AA",
              sort_order: 0,
              group_id: groupFilter || groups[0]?.id,
            });
          }}
        >
          + Шинэ сэдэв
        </Button>
      </div>

      {/* Ашиглалтын товч заавар — сэдэв дээр дарж дэлгэрэнгүй рүү ордгийг
          хэрэглэгч мэддэггүй байсан тул алхмаар нь тайлбарлав. */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 10, padding: "6px 12px", borderRadius: 8 }}
        message={<b style={{ fontSize: 12 }}>Хэрхэн ашиглах вэ</b>}
        description={
          <div style={{ fontSize: 11.5, lineHeight: 1.6 }}>
            <b>1.</b> «+ Шинэ сэдэв» дарж сэдвээ үүсгэнэ (групп, ангилал заавал).
            <br />
            <b>2.</b> Хүснэгтийн <b>Сэдвийн нэр дээр дарж</b>{" "}
            <i className="fas fa-folder-open" style={{ fontSize: 10 }} /> дэлгэрэнгүй
            рүү орно — тэндээс <b>бичлэг</b> (3–10 минут) болон{" "}
            <b>асуулт</b>-аа нэмнэ.
            <br />
            <b>3.</b> «Бичлэг», «Асуулт» баганын тоо 0 байвал тэр сэдвийг долоо
            хоногийн хуваарьт оруулах боломжгүй.
            <br />
            <b>4.</b> Долоо хоног бүрийн сэдвийг «Хуваарь» табаас товлоно.
          </div>
        }
      />

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={shownTopics}
        columns={columns}
        pagination={{
          pageSize,
          onShowSizeChange: (_c, size) => setPageSize(size),
          onChange: (_p, size) => size !== pageSize && setPageSize(size),
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}–${range[1]} / нийт ${total}`,
          size: "small",
        }}
      />

      <Modal
        open={!!topicModal}
        title={topicModal?.id ? "Сэдэв засах" : "Шинэ сэдэв"}
        onCancel={() => setTopicModal(null)}
        onOk={saveTopic}
        okText="Хадгалах"
        cancelText="Болих"
      >
        <Form form={topicForm} layout="vertical">
          <Form.Item
            name="name"
            label="Сэдвийн нэр"
            rules={[{ required: true, message: "Нэрийг бичнэ үү." }]}
          >
            <Input placeholder="Жишээ: Шатны аюулгүй байдал" />
          </Form.Item>
          <Form.Item
            name="group_id"
            label="Мэдлэгийн сангийн групп"
            rules={[{ required: true, message: "Группээ сонгоно уу." }]}
            extra="Групп /knowledge/reference хуудсаас удирдагдана."
          >
            <Select
              placeholder="Групп сонгох"
              options={groups.map((g) => ({ value: g.id, label: groupLabel(g) }))}
            />
          </Form.Item>
          <Form.Item name="category_code" label="Ангилал">
            <Select
              options={(categories.length
                ? categories
                : [
                    { code: "AA", name: "Ажлын аюулгүй байдал" },
                    { code: "EA", name: "Эрүүл ахуй, эрүүл мэнд" },
                  ]
              ).map((c) => ({ value: c.code, label: c.name }))}
            />
          </Form.Item>
          <Form.Item name="description" label="Тайлбар">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="source_ref" label="Эх сурвалж (журам/заалт)">
            <Input placeholder="Жишээ: ХЭМАБМТ-06, 3.2" />
          </Form.Item>
          <Form.Item name="sort_order" label="Эрэмбэ">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      {detail ? (
        <TopicDetailModal
          detail={detail}
          onClose={() => setDetail(null)}
          onChanged={async () => {
            setDetail(await knowledgeApi.topic(detail.id));
            load();
            onDataChanged && onDataChanged();
          }}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
//  Сэдвийн дэлгэрэнгүй — бичлэг + асуулт
// ---------------------------------------------------------------------------
/**
 * @param openContentDay — нээгдэхдээ ШУУД «Шинэ бичлэг» маягтыг тухайн
 *   өдрөөр бэлдэж нээх (1..7). Хуваарийн «Өдрүүд» баганаас хоосон өдөр
 *   дарахад ашиглагдана — админ сэдвээ хайж олох шаардлагагүй.
 */
function TopicDetailModal({ detail, onClose, onChanged, openContentDay = null }) {
  const [uploading, setUploading] = useState(0);
  const [contentForm] = Form.useForm();
  const [questionForm] = Form.useForm();
  const [contentModal, setContentModal] = useState(null);
  const [questionModal, setQuestionModal] = useState(null);
  const fileRef = useRef(null);
  const [uploaded, setUploaded] = useState(null);

  /**
   * Гаднаас «энэ өдөрт бичлэг нэм» гэж ирвэл маягтыг өөрөө нээнэ.
   *
   * ⚠ НЭГ УДАА л ажиллана (`ref`). Эс бөгөөс хэрэглэгч маягтыг хаах бүрд
   *   дахин нээгдэж, гарах боломжгүй давталт үүснэ.
   */
  const autoOpenRef = useRef(null);
  useEffect(() => {
    if (!detail || !openContentDay) return;
    const key = `${detail.id}-${openContentDay}`;
    if (autoOpenRef.current === key) return;
    autoOpenRef.current = key;
    setContentModal({});
    contentForm.resetFields();
    contentForm.setFieldsValue({
      content_type: "video",
      channel: "app",
      day_no: openContentDay,
    });
    setUploaded(null);
  }, [detail, openContentDay, contentForm]);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Хэт том файлыг БАЙРШУУЛАХААС ӨМНӨ анхааруулна — сүлжээгээр дэмий
    // дамжуулахаас сэргийлж, админд бодит тоог харуулна.
    // Логик нь components/MediaUpload.js-д НЭГ дор (16 хуудсад хуваалцана).
    if (!(await guardFile(file))) {
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    setUploading(1);
    try {
      const res = await knowledgeApi.upload(file, setUploading);
      setUploaded(res);
      contentForm.setFieldsValue({
        file_url: res.file_url,
        file_name: res.file_name,
        file_size: res.file_size,
        mime_type: res.mime_type,
        content_type: res.content_type,
        title: contentForm.getFieldValue("title") || res.file_name,
      });
      // Сервер талын зөөлөн анхааруулга — байршсан ч админ мэдэж байх ёстой.
      if (!showUploadWarning(res)) message.success("Файл байршлаа.");
    } catch (ex) {
      message.error(ex?.response?.data?.message || "Файл байршуулж чадсангүй.");
    } finally {
      setUploading(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveContent = async () => {
    try {
      const values = await contentForm.validateFields();
      const payload = { ...values, topic_id: detail.id };
      if (contentModal?.id) {
        await knowledgeApi.updateContent(contentModal.id, payload);
      } else {
        if (!payload.file_url) {
          message.warning("Эхлээд файлаа байршуулна уу.");
          return;
        }
        await knowledgeApi.createContent(payload);
      }
      message.success("Хадгаллаа.");
      setContentModal(null);
      setUploaded(null);
      onChanged();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || "Хадгалж чадсангүй.");
    }
  };

  const saveQuestion = async () => {
    try {
      const values = await questionForm.validateFields();
      const payload = { ...values, topic_id: detail.id };
      if (questionModal?.id) {
        await knowledgeApi.updateQuestion(questionModal.id, payload);
      } else {
        await knowledgeApi.createQuestion(payload);
      }
      message.success("Хадгаллаа.");
      setQuestionModal(null);
      onChanged();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || "Хадгалж чадсангүй.");
    }
  };

  return (
    <Modal
      open
      width={980}
      title={<span style={{ fontWeight: 800 }}>{detail.name}</span>}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Хаах</Button>}
    >
      <Tabs defaultActiveKey="contents" items={[
        { key: "contents", label: `Бичлэг (${(detail.contents || []).length})`, children: (<>
          <Button
            type="primary"
            size="small"
            style={{ background: PRIMARY, borderColor: PRIMARY, marginBottom: 10 }}
            onClick={() => {
              setContentModal({});
              contentForm.resetFields();
              contentForm.setFieldsValue({ content_type: "video", channel: "app" });
              setUploaded(null);
            }}
          >
            + Бичлэг нэмэх
          </Button>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={detail.contents || []}
            columns={[
              { title: "Гарчиг", dataIndex: "title" },
              {
                title: "Төрөл",
                dataIndex: "content_type",
                width: 110,
                render: (v) => <Tag>{v}</Tag>,
              },
              {
                title: "Өдөр",
                dataIndex: "day_no",
                width: 100,
                render: (v) => (v ? DAY_NAMES[v - 1] : <Tag>Бүх өдөр</Tag>),
              },
              {
                title: "Үргэлжлэх",
                dataIndex: "duration_sec",
                width: 100,
                render: (v) => (v ? `${v} сек` : "-"),
              },
              {
                title: "",
                width: 150,
                render: (_, r) => (
                  <Space>
                    {r.file_url ? (
                      <a href={r.file_url} target="_blank" rel="noreferrer">
                        Үзэх
                      </a>
                    ) : null}
                    <Button
                      size="small"
                      onClick={() => {
                        setContentModal(r);
                        contentForm.setFieldsValue(r);
                      }}
                    >
                      Засах
                    </Button>
                    <Popconfirm
                      title="Устгах уу?"
                      onConfirm={async () => {
                        await knowledgeApi.deleteContent(r.id);
                        onChanged();
                      }}
                    >
                      <Button size="small" danger>
                        Устгах
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </>) },

        { key: "questions", label: `Асуулт (${(detail.questions || []).length})`, children: (<>
          <Button
            type="primary"
            size="small"
            style={{ background: PRIMARY, borderColor: PRIMARY, marginBottom: 10 }}
            onClick={() => {
              setQuestionModal({});
              questionForm.resetFields();
              questionForm.setFieldsValue({ correct_option: "A", points: 5 });
            }}
          >
            + Асуулт нэмэх
          </Button>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={detail.questions || []}
            columns={[
              { title: "Асуулт", dataIndex: "question_text" },
              {
                title: "Өдөр",
                dataIndex: "day_no",
                width: 100,
                render: (v) => (v ? DAY_NAMES[v - 1] : <Tag>Дарааллаар</Tag>),
              },
              {
                title: "Зөв",
                dataIndex: "correct_option",
                width: 70,
                align: "center",
                render: (v) => <Tag color="green">{v}</Tag>,
              },
              { title: "Оноо", dataIndex: "points", width: 70, align: "center" },
              {
                title: "",
                width: 150,
                render: (_, r) => (
                  <Space>
                    <Button
                      size="small"
                      onClick={() => {
                        setQuestionModal(r);
                        questionForm.setFieldsValue(r);
                      }}
                    >
                      Засах
                    </Button>
                    <Popconfirm
                      title="Устгах уу?"
                      onConfirm={async () => {
                        await knowledgeApi.deleteQuestion(r.id);
                        onChanged();
                      }}
                    >
                      <Button size="small" danger>
                        Устгах
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </>) },
      ]} />

      {/* Бичлэгийн модал */}
      <Modal
        open={!!contentModal}
        title={contentModal?.id ? "Бичлэг засах" : "Шинэ бичлэг"}
        onCancel={() => setContentModal(null)}
        onOk={saveContent}
        okText="Хадгалах"
        cancelText="Болих"
      >
        <Form form={contentForm} layout="vertical">
          {!contentModal?.id ? (
            <Form.Item label="Файл (видео / зураг)">
              <input
                ref={fileRef}
                type="file"
                accept="video/*,image/*"
                onChange={onPickFile}
              />
              {uploading ? (
                <Progress percent={uploading} strokeWidth={8} strokeColor={PRIMARY} />
              ) : null}
              {uploaded ? (
                <Tag color="green" style={{ marginTop: 6 }}>
                  {uploaded.file_name} ·{" "}
                  {Math.round((uploaded.file_size || 0) / 1024 / 1024)} MB
                </Tag>
              ) : null}
            </Form.Item>
          ) : null}
          <Form.Item name="file_url" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="file_name" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="file_size" hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name="mime_type" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="title"
            label="Гарчиг"
            rules={[{ required: true, message: "Гарчгийг бичнэ үү." }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="content_type" label="Төрөл" required>
            <Select options={CONTENT_TYPES} />
          </Form.Item>
          {/* ⚠ ӨДӨР ЗААВАЛ. Урьд нь хоосон үлдээвэл «долоо хоногийн бүх
              өдөр» харагддаг байсан тул НЭГ бичлэг 7 хоног ажиллаж,
              ажилтан даваагаас баасан хүртэл ижил бичлэг үздэг байв.
              «Минутын мэдлэг» нь ӨДӨР ТУТМЫН дасгал — өдөр бүр өөрийн
              бичлэгтэй байна. */}
          <Form.Item
            name="day_no"
            label="Аль өдөр харагдах"
            rules={[{ required: true, message: "Өдрийг сонгоно уу." }]}
            extra="Өдөр бүр өөрийн бичлэгтэй — 7 хоногт нэг бичлэг тавихаа больсон."
          >
            <Select
              options={DAY_NAMES.map((d, i) => ({ value: i + 1, label: d }))}
            />
          </Form.Item>
          <Form.Item name="duration_sec" label="Үргэлжлэх хугацаа (сек)">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="description" label="Тайлбар">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Асуултын модал */}
      <Modal
        open={!!questionModal}
        title={questionModal?.id ? "Асуулт засах" : "Шинэ асуулт"}
        onCancel={() => setQuestionModal(null)}
        onOk={saveQuestion}
        okText="Хадгалах"
        cancelText="Болих"
      >
        <Form form={questionForm} layout="vertical">
          <Form.Item
            name="question_text"
            label="Асуулт"
            rules={[{ required: true, message: "Асуултыг бичнэ үү." }]}
          >
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="option_a"
            label="A хариулт"
            rules={[{ required: true, message: "A хариултыг бичнэ үү." }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="option_b"
            label="B хариулт"
            rules={[{ required: true, message: "B хариултыг бичнэ үү." }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="option_c" label="C хариулт">
            <Input />
          </Form.Item>
          <Form.Item name="option_d" label="D хариулт">
            <Input />
          </Form.Item>
          <Form.Item name="correct_option" label="Зөв хариулт" required>
            <Radio.Group options={OPTION_KEYS} optionType="button" />
          </Form.Item>
          <Form.Item name="explanation" label="Тайлбар (хариулсны дараа харагдана)">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="day_no" label="Аль өдрийн асуулт" extra="Хоосон бол дарааллаар нь өдөр бүр ээлжилнэ.">
            <Select
              allowClear
              options={DAY_NAMES.map((d, i) => ({ value: i + 1, label: d }))}
            />
          </Form.Item>
          <Form.Item name="points" label="Safety Point (5-10)" required>
            <InputNumber min={0} max={100} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
}

// ===========================================================================
//  7 хоногийн хуваарь
// ===========================================================================
// ===========================================================================
//  ХУВААРЬ — САРЫН ХУАНЛИ, ӨДӨР ТУС БҮРД СЭДЭВ
//
//  ⚠ Урьд нь 53 мөрт хүснэгт байсан бөгөөд сэдэв нь ДОЛОО ХОНОГТ
//    оногддог байв. Одоо «Минутын мэдлэг» нь ӨДӨР БҮР өөрийн бичлэгтэй
//    тул хуваарь ч өдөр тутмын болов:
//        knowledge_week.day_no = 1..7  → ЗӨВХӨН тэр өдөрт
//        knowledge_week.day_no = NULL  → долоо хоног бүхэлдээ (нөөц зам)
//    Өдрийн мөр нь долоо хоногийнхоос ДАВУУ.
// ===========================================================================

/** Огнооны ISO (жил, 7хоног, өдөр). Даваа = 1. */
function isoOf(dt) {
  const d = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
  const dayNum = d.getUTCDay() || 7;
  // Долоо хоногийн ПҮРЭВ гараг нь ISO жилийг тодорхойлно
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return { y: d.getUTCFullYear(), w: week, d: dayNum };
}

/** `toISOString()` нь UTC+8-д 16:00-оос хойш ӨМНӨХ ӨДРИЙГ буцаадаг. */
const ymd = (dt) =>
  `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
    dt.getDate()
  ).padStart(2, "0")}`;

const MONTH_NAMES = [
  "1-р сар",
  "2-р сар",
  "3-р сар",
  "4-р сар",
  "5-р сар",
  "6-р сар",
  "7-р сар",
  "8-р сар",
  "9-р сар",
  "10-р сар",
  "11-р сар",
  "12-р сар",
];

function WeekPanel({ onDataChanged }) {
  const today = new Date();
  const thisYear = today.getFullYear();
  const [year, setYear] = useState(thisYear);
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [topics, setTopics] = useState([]);
  const [groups, setGroups] = useState([]);
  // Аль группын хуваарийг харж байна — групп бүр өөрийн сэдэвтэй.
  const [groupId, setGroupId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Дарсан өдрийн цонх — нүдний түлхүүр (огноо ymd)
  const [dayCell, setDayCell] = useState(null);
  /* ⚠ ӨӨР САРЫН СЭДЭВ (2026-09-30): сэдвийн сонголт нь ЗӨВХӨН тухайн
     сарын (группын) сэдвээр хязгаарлагддаг байв. Гэвч нэг сэдвийг өөр
     сард давтах, эсвэл өмнөх сарын бичлэгийг ашиглах шаардлага бодитоор
     гардаг. Тэр үед админ бүлгээ түр солиод буцаах хэрэгтэй болдог —
     хуваарь нь өөр сар руу шилжчихдэг тул андуурахад хялбар.

     `null`  → хуваарийн тухайн сарынх (анхдагч, андуурал багатай)
     `"all"` → БҮХ сар; тэр үед сонголтууд САРААР БҮЛЭГЛЭГДЭНЭ
     бусад   → тухайн группын id
     ⚠ «Бүх сар» дээр 360+ сэдэв нэг хавтгай жагсаалт болж уншигдахаа
       болино — тиймээс бүлэглэлт нь гоо сайхан биш, ХЭРЭГЦЭЭ. */
  const [topicGroupPick, setTopicGroupPick] = useState(null);

  /* ⚠ Хуваарийн бүлэг (= сар) солигдоход сонголтыг АНХДАГЧ руу буцаана.
     Эс бөгөөс админ нэг өдөрт «5-р сар» сонгоод, дараа нь хуанлиа 9-р сар
     руу нүүлгэхэд сэдвийн сонголт 5-р сар дээрээ тогтоод үлдэж, дараагийн
     өдрүүдэд ЧИМЭЭГҮЙ буруу сарын сэдэв санал болгоно. */
  useEffect(() => {
    setTopicGroupPick(null);
  }, [groupId]);

  /**
   * Сэдвийн дэлгэрэнгүйг нээж, ШУУД тэр өдрийн бичлэг нэмэх маягтыг
   * гаргана. Урьд нь админ цоорхойг хараад «Сэдэв» таб руу очиж, нэрээр
   * хайж олоод, дараа нь өдрөө сонгох хэрэгтэй байв — 4 алхам.
   */
  const [detail, setDetail] = useState(null);
  const [openDay, setOpenDay] = useState(null);

  const openTopicForDay = async (topicId, day) => {
    if (!topicId) return;
    try {
      const d = await knowledgeApi.topic(topicId);
      setOpenDay(day);
      setDetail(d);
    } catch (e) {
      message.error(e?.response?.data?.message || "Сэдвийг нээж чадсангүй.");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, t] = await Promise.all([
        knowledgeApi.weeks(year, groupId || undefined, month),
        knowledgeApi.topics(),
      ]);
      setData(w);
      setTopics(t.topics || []);
      const gs = t.groups || w.groups || [];
      setGroups(gs);
      /**
       * Групп сонгоогүй бол ХАРЖ БУЙ САРЫН бүлгийг сонгоно.
       * (Өмнө нь эхнийхийг — 365 хоногийн микро сургалтад эхний групп нь
       *  ҮРГЭЛЖ 1-р сар байдаг тул 9-р сард ажиллаж буй хүн бүр гараар
       *  группээ солих хэрэгтэй болдог байв.)
       */
      if (!groupId && gs.length) {
        const cur = gs.find((g) => g.month_no === month);
        setGroupId((cur || gs[0]).id);
      }
    } catch (e) {
      message.error(e?.response?.data?.message || "Хуваарийг татаж чадсангүй.");
    } finally {
      setLoading(false);
    }
  }, [year, month, groupId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Сар солиход ТЭР САРЫН бүлгийг дагуулж сонгоно.
   *
   * ⚠ Зөвхөн сар солих ҮЙЛДЭЛ дээр л ажиллана (useEffect биш). Эс бөгөөс
   *   админ гараар өөр групп сонгосныг тэр даруй дарж бичих байв.
   */
  const goMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) {
      m = 12;
      y -= 1;
    } else if (m > 12) {
      m = 1;
      y += 1;
    }
    setMonth(m);
    setYear(y);
    const g = groups.find((x) => x.month_no === m);
    if (g) setGroupId(g.id);
  };

  /**
   * Мөрийн ХАЙЛТЫН ХҮСНЭГТ — түлхүүр нь `жил-7хоног`.
   * Нэг долоо хоногт долоо хоногийн мөр + 7 өдрийн мөр байж болно.
   */
  const byWeek = useMemo(() => {
    const m = {};
    (data?.weeks || []).forEach((w) => {
      const k = `${w.year_no}-${w.week_no}`;
      (m[k] = m[k] || []).push(w);
    });
    return m;
  }, [data]);

  /** Тухайн өдөрт хүчинтэй мөр — ӨДРИЙНХ нь долоо хоногийнхоос ДАВУУ. */
  const rowFor = useCallback(
    (iso) => {
      const list = byWeek[`${iso.y}-${iso.w}`] || [];
      return (
        list.find((r) => Number(r.day_no) === iso.d) ||
        list.find((r) => r.day_no === null || r.day_no === undefined) ||
        null
      );
    },
    [byWeek]
  );

  /** Сарын хуанлийн нүднүүд — бүтэн долоо хоногийн мөрөөр. */
  const cells = useMemo(() => {
    const todayKey = ymd(new Date());
    const first = new Date(year, month - 1, 1);
    const start = new Date(first);
    start.setDate(first.getDate() - ((first.getDay() || 7) - 1));
    const last = new Date(year, month, 0);
    const end = new Date(last);
    end.setDate(last.getDate() + (7 - (last.getDay() || 7)));
    const out = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const date = new Date(d);
      const iso = isoOf(date);
      const row = rowFor(iso);
      out.push({
        key: ymd(date),
        date,
        iso,
        row,
        inMonth: date.getMonth() === month - 1,
        isToday: ymd(date) === todayKey,
        // Backend-ийн KnowledgeContent.for_day-тай ИЖИЛ дүрэм:
        //   яг тэр гараг → гараг заагаагүй (бүх өдөрт) → сэдэв ӨДӨРТ
        //   дангаар оноогдсон бол сэдвийн аль ч бичлэг.
        // ⚠ Өмнө нь зөвхөн гарагийн дугаар таарахыг шалгадаг байсан тул
        //   лхагвад оруулсан бичлэгтэй сэдвийг даваад оноовол «Бичлэг
        //   АЛГА» гэж харагдаж, ажилтанд ч гарахгүй байв (2026-09-21).
        hasContent:
          !!row &&
          ((row.content_days || []).includes(iso.d) ||
            !!row.content_all_days ||
            (row.day_no != null && !!row.content_any)),
        hasQuestion:
          !!row &&
          ((row.question_days || []).includes(iso.d) ||
            !!row.question_all_days ||
            !!row.question_any),
        // Долоо хоног бүхэлдээ оноосон мөрөөс УРГАСАН эсэх — нүдэнд
        // тэмдэглэнэ (тэр өдрийг дангаар нь засаагүй гэсэн үг).
        // ⚠ `=== null` гэж бичиж БОЛОХГҮЙ. `day_no` талбар нь хуучин
        //   backend дээр огт ИРЭХГҮЙ (undefined) тул бүх өдөр «өөрийн
        //   оноолттой» мэт харагдаж, «7 хоногийн» тэмдэглэгээ алга болно.
        inherited: !!row && (row.day_no === null || row.day_no === undefined),
      });
    }
    return out;
  }, [year, month, rowFor]);

  /* Сэдвийн сонголт. Анхдагчаар тухайн группынх (= тухайн сар); унтраалга
     асаахад БҮХ сараас. */
  const pickedGroup =
    topicGroupPick === null ? groupId : topicGroupPick;

  const groupTopics = useMemo(() => {
    if (pickedGroup === "all" || !pickedGroup) return topics;
    return topics.filter((t) => String(t.group_id) === String(pickedGroup));
  }, [topics, pickedGroup]);

  /** Группын id → «N-р сар» шошго (өөр сарын сэдвийг ялгахад).
   *  ⚠ Нэрийг `groupLabel` гэж БОЛОХГҮЙ: модулийн түвшинд ижил нэртэй
   *    ФУНКЦ бий бөгөөд энэ компонент дотроос дуудагддаг. Далдалбал
   *    обьектыг функц мэт дуудаж ажиллуулах үед унана (lint барихгүй). */
  const monthOfGroup = useMemo(() => {
    const m = {};
    (groups || []).forEach((g) => {
      if (g.month_no) {
        m[g.id] = `${g.month_no}-р сар`;
        return;
      }
      /* Сар тодорхойгүй бүлэг (хуучин бүлгүүд) — нэрээр нь. Нэр урт байж
         болох тул тайрна: сонголтын мөр хэт уртсаад сэдвийн нэр нь
         харагдахгүй болно. */
      const nm = groupLabel(g) || "";
      m[g.id] = nm.length > 18 ? `${nm.slice(0, 18)}…` : nm;
    });
    return m;
  }, [groups]);

  const activeGroup = groups.find((g) => g.id === groupId) || null;
  const groupColor = activeGroup?.color_hex || PRIMARY;

  /**
   * ХЯНАЛТЫН ҮЗҮҮЛЭЛТ — «энэ сар хэр бэлэн вэ».
   *
   * ⚠ Зөвхөн ЭНЭ САРЫН өдрүүдээр тоолно (хуанлийн захын өдрүүд нь хөрш
   *   сарынх бөгөөд тэдгээрийг оруулбал хувь нь худал гарна).
   *
   * ⚠ «Сэдэвтэй» гэдэг нь ХАНГАЛТГҮЙ. Тухайн сэдэвт ТЭР ӨДРИЙН бичлэг
   *   байхгүй бол ажилтан юу ч үзэхгүй (KnowledgeContent.for_day нь яг
   *   тэр өдрийнхийг л буцаана). Тиймээс хоёуланг нь тусад нь тоолно.
   */
  const stats = useMemo(() => {
    const mine = cells.filter((c) => c.inMonth);
    const withTopic = mine.filter((c) => c.row?.topic_id);
    const withContent = mine.filter((c) => c.hasContent);
    return {
      total: mine.length,
      topic: withTopic.length,
      content: withContent.length,
      empty: mine.length - withTopic.length,
      percent: mine.length
        ? Math.round((withContent.length / mine.length) * 100)
        : 0,
    };
  }, [cells]);

  const todayCell = cells.find((c) => c.isToday && c.inMonth) || null;

  /** Сэдэв оноох. `dayNo` өгвөл ЗӨВХӨН тэр өдөрт, эс бөгөөс 7 хоногт. */
  const assign = async (iso, topic_id, dayNo) => {
    setSaving(true);
    try {
      await knowledgeApi.setWeek({
        year_no: iso.y,
        week_no: iso.w,
        topic_id,
        group_id: groupId || undefined,
        day_no: dayNo === null ? undefined : dayNo,
      });
      message.success(
        dayNo ? "Тухайн өдрийн сэдэв тохирлоо." : "7 хоногийн сэдэв тохирлоо."
      );
      await load();
      onDataChanged && onDataChanged();
      setDayCell(null);
    } catch (e) {
      message.error(e?.response?.data?.message || "Хадгалж чадсангүй.");
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async (row) => {
    if (!row?.id) return;
    setSaving(true);
    try {
      await knowledgeApi.deleteWeek(row.id);
      message.success("Оноолт устлаа.");
      await load();
      onDataChanged && onDataChanged();
      setDayCell(null);
    } catch (e) {
      message.error(e?.response?.data?.message || "Устгаж чадсангүй.");
    } finally {
      setSaving(false);
    }
  };

  const cur = dayCell ? cells.find((c) => c.key === dayCell) : null;

  return (
    <>
      <Space style={{ marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 700 }}>Групп:</span>
        <Select
          value={groupId}
          style={{ width: 250 }}
          placeholder="Групп сонгох"
          onChange={setGroupId}
          showSearch
          optionFilterProp="label"
          options={groups.map((g) => ({
            value: g.id,
            label:
              g.month_no === month
                ? `${groupLabel(g)} — энэ сар`
                : groupLabel(g),
          }))}
        />

        {/* ── САР СОЛИХ ────────────────────────────────────────────── */}
        <Button
          onClick={() => goMonth(-1)}
          icon={<i className="fas fa-chevron-left" />}
        />
        <Select
          value={month}
          style={{ width: 120 }}
          onChange={(m) => {
            setMonth(m);
            const g = groups.find((x) => x.month_no === m);
            if (g) setGroupId(g.id);
          }}
          options={MONTH_NAMES.map((n, i) => ({ value: i + 1, label: n }))}
        />
        <Select
          value={year}
          style={{ width: 100 }}
          onChange={setYear}
          options={[thisYear - 1, thisYear, thisYear + 1].map((y) => ({
            value: y,
            label: y,
          }))}
        />
        <Button
          onClick={() => goMonth(1)}
          icon={<i className="fas fa-chevron-right" />}
        />
        <Button
          onClick={() => {
            setYear(thisYear);
            setMonth(new Date().getMonth() + 1);
          }}
        >
          Өнөөдөр
        </Button>
      </Space>

      {/* ── ХЯНАЛТЫН ХЭСЭГ ────────────────────────────────────────────
          «Энэ сар хэр бэлэн вэ» гэдгийг НЭГ харцаар. */}
      <Card
        bodyStyle={{ padding: "10px 14px" }}
        style={{ marginBottom: 12, borderLeft: `4px solid ${groupColor}` }}
      >
        <div
          style={{
            display: "flex",
            gap: 18,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 6,
                background: groupColor,
                display: "inline-block",
              }}
            />
            <b>{activeGroup?.name || "Групп сонгоогүй"}</b>
            <Tag
              color={
                month === CURRENT_MONTH && year === thisYear ? "gold" : "default"
              }
            >
              {year} · {MONTH_NAMES[month - 1]}
            </Tag>
          </span>

          <span style={{ fontSize: 12 }}>
            Сэдэвтэй{" "}
            <b style={{ color: PRIMARY }}>
              {stats.topic}/{stats.total}
            </b>
          </span>
          <span style={{ fontSize: 12 }}>
            Бичлэгтэй{" "}
            <b style={{ color: stats.percent === 100 ? "#15803D" : PRIMARY }}>
              {stats.content}/{stats.total}
            </b>{" "}
            <Tag
              color={
                stats.percent === 100
                  ? "green"
                  : stats.percent >= 50
                    ? "blue"
                    : "orange"
              }
            >
              {stats.percent}%
            </Tag>
          </span>

          {todayCell ? (
            <Tag color={todayCell.hasContent ? "green" : "red"}>
              Өнөөдөр:{" "}
              {todayCell.hasContent
                ? "бичлэгтэй"
                : todayCell.row?.topic_id
                  ? "сэдэвтэй ч БИЧЛЭГГҮЙ"
                  : "СЭДЭВГҮЙ"}
            </Tag>
          ) : null}

          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: "#5F5E5A" }}>
            Өдөр дээр дарж сэдэв оноож, бичлэг нэмнэ.
          </span>
        </div>
      </Card>

      {/* ── ХУАНЛИ ──────────────────────────────────────────────────── */}
      <Spin spinning={loading}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 6,
          }}
        >
          {DAY_NAMES.map((n, i) => (
            <div
              key={n}
              style={{
                textAlign: "center",
                fontWeight: 700,
                fontSize: 12,
                padding: "6px 0",
                color: i >= 5 ? "#B42318" : "#5F5E5A",
              }}
            >
              {n}
            </div>
          ))}

          {cells.map((c) => {
            const t = c.row?.topic;
            /**
             * ⚠ Өнгө ГАНЦААРАА мэдээлэл дамжуулахгүй:
             *   • бичлэгтэй        → дүүрэн цэг + tooltip
             *   • зөвхөн асуулттай → хүрээтэй цэг
             *   • хоосон           → тасархай хүрээтэй нүд + «+»
             */
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setDayCell(c.key)}
                title={`${c.date.getDate()} · ${
                  t ? t.name : "сэдэв оноогоогүй"
                }`}
                style={{
                  textAlign: "left",
                  cursor: "pointer",
                  minHeight: 84,
                  padding: "6px 8px",
                  borderRadius: 8,
                  background: c.inMonth
                    ? c.hasContent
                      ? `${groupColor}12`
                      : "#fff"
                    : "#FAFAFA",
                  opacity: c.inMonth ? 1 : 0.45,
                  border: c.isToday
                    ? `2px solid ${PRIMARY}`
                    : c.row?.topic_id
                      ? `1px solid ${groupColor}55`
                      : "1px dashed #D0D5DD",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <b
                    style={{
                      fontSize: 13,
                      color: c.isToday
                        ? PRIMARY
                        : c.iso.d >= 6
                          ? "#B42318"
                          : "#1F2430",
                    }}
                  >
                    {c.date.getDate()}
                  </b>
                  <span style={{ display: "flex", gap: 3 }}>
                    {c.hasContent ? (
                      <span
                        title="Бичлэгтэй"
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 4,
                          background: groupColor,
                        }}
                      />
                    ) : null}
                    {c.hasQuestion ? (
                      <span
                        title="Асуулттай"
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 4,
                          border: `1px solid ${groupColor}`,
                        }}
                      />
                    ) : null}
                  </span>
                </div>

                {t ? (
                  <span
                    style={{
                      fontSize: 11,
                      lineHeight: 1.25,
                      color: "#1F2430",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      fontStyle: c.inherited ? "italic" : "normal",
                    }}
                  >
                    {t.name}
                  </span>
                ) : (
                  <span style={{ fontSize: 16, color: "#C3C9D4" }}>+</span>
                )}

                {c.inherited && t ? (
                  <span style={{ fontSize: 9, color: "#9AA3B0" }}>
                    7 хоногийн
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </Spin>

      {/* ── НЭГ ӨДРИЙН ЦОНХ ─────────────────────────────────────────── */}
      <Modal
        open={!!cur}
        onCancel={() => setDayCell(null)}
        footer={null}
        width={620}
        title={
          cur
            ? `${cur.date.getFullYear()} оны ${
                cur.date.getMonth() + 1
              }-р сарын ${cur.date.getDate()} · ${DAY_NAMES[cur.iso.d - 1]}`
            : ""
        }
      >
        {cur ? (
          <>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 6,
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontWeight: 700 }}>Энэ өдрийн сэдэв</span>
              {/* ── АЛЬ САРЫН СЭДВЭЭС СОНГОХ ──────────────────────────
                  Анхдагчаар хуваарийн тухайн сар. Өөр сарын бичлэгийг
                  ашиглах шаардлага гарвал эндээс шилжинэ — бүлгээ түр
                  солиод буцах шаардлагагүй (хуваарь нь байрандаа үлдэнэ). */}
              <Select
                size="small"
                value={topicGroupPick === null ? groupId : topicGroupPick}
                onChange={(v) => setTopicGroupPick(v)}
                style={{ minWidth: 150, flex: "0 1 auto" }}
                options={[
                  ...groups.map((g) => ({
                    value: g.id,
                    label:
                      g.id === groupId
                        ? `${monthOfGroup[g.id]} · энэ сар`
                        : monthOfGroup[g.id],
                  })),
                  { value: "all", label: `Бүх сар (${topics.length})` },
                ]}
              />
            </div>
            <Select
              showSearch
              allowClear
              loading={saving}
              value={cur.row?.topic_id}
              placeholder="Сэдэв сонгох"
              style={{ width: "100%" }}
              optionFilterProp="label"
              listHeight={320}
              onChange={(v) => v && assign(cur.iso, v, cur.iso.d)}
              /* ⚠ «Бүх сар» үед САРААР БҮЛЭГЛЭНЭ. 360+ сэдвийг хавтгай
                 жагсаалтаар өгвөл гүйлгэж олох боломжгүй болно; хайлт нь
                 бүлэг дотуур ажиллаж, толгой нь аль сарынх болохыг хэлнэ. */
              options={
                pickedGroup === "all"
                  ? groups
                      .map((g) => ({
                        label: monthOfGroup[g.id],
                        options: topics
                          .filter((t) => String(t.group_id) === String(g.id))
                          .map((t) => ({
                            value: t.id,
                            label: `${t.code} · ${t.name}`,
                          })),
                      }))
                      .filter((grp) => grp.options.length)
                  : groupTopics.map((t) => ({
                      value: t.id,
                      label: `${t.code} · ${t.name}`,
                    }))
              }
            />

            <div style={{ fontSize: 12, color: "#5F5E5A", marginTop: 6 }}>
              Сонгосон сэдэв ЗӨВХӨН энэ өдөрт оногдоно ({cur.iso.w}-р 7 хоног).
            </div>

            {cur.inherited && cur.row?.topic_id ? (
              <div
                style={{
                  marginTop: 10,
                  padding: "8px 10px",
                  borderRadius: 6,
                  background: "#FFF7E6",
                  fontSize: 12,
                }}
              >
                Одоо энэ өдөр нь <b>7 хоног бүхэлдээ</b> оноосон сэдвийг дагаж
                байна. Дээрээс сонгоход зөвхөн энэ өдөр салж, бусад өдөр хэвээр
                үлдэнэ.
              </div>
            ) : null}

            {/* ── ТУХАЙН ӨДРИЙН АГУУЛГА ──────────────────────────────
                ⚠ Сэдэв оноогдсон ч ТЭР ӨДРИЙН бичлэг байхгүй бол ажилтан
                  юу ч үзэхгүй. Тиймээс энэ мөр ЗААВАЛ харагдана. */}
            {cur.row?.topic_id ? (
              <div style={{ marginTop: 14 }}>
                <Space size={8} wrap>
                  <Tag color={cur.hasContent ? "green" : "red"}>
                    {cur.hasContent ? "Бичлэгтэй" : "Бичлэг АЛГА"}
                  </Tag>
                  <Tag color={cur.hasQuestion ? "green" : "default"}>
                    {cur.hasQuestion ? "Асуулттай" : "Асуулт алга"}
                  </Tag>
                </Space>
                <div style={{ marginTop: 10 }}>
                  <Space wrap>
                    <Button
                      type="primary"
                      onClick={() => {
                        const tid = cur.row.topic_id;
                        const dno = cur.iso.d;
                        setDayCell(null);
                        openTopicForDay(tid, dno);
                      }}
                    >
                      {cur.hasContent ? "Бичлэг засах / нэмэх" : "Бичлэг нэмэх"}
                    </Button>
                    <Button
                      onClick={() => assign(cur.iso, cur.row.topic_id, null)}
                      disabled={saving}
                    >
                      7 хоног бүхэлд нь оноох
                    </Button>
                    {cur.row.id && !cur.inherited ? (
                      <Button
                        danger
                        onClick={() => removeRow(cur.row)}
                        disabled={saving}
                      >
                        Энэ өдрийн оноолтыг устгах
                      </Button>
                    ) : null}
                  </Space>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </Modal>

      {/* Өдрийн нүднээс нээгдсэн сэдвийн дэлгэрэнгүй — бичлэгийн маягт
          ТУХАЙН ӨДРӨӨР бэлдэгдсэн байдлаар шууд нээгдэнэ.

          ⚠ `detail` БАЙХАД Л зурна. `TopicDetailModal` нь `detail`-ыг
            хоосон байж болно гэж үздэггүй (`detail.contents` гэх мэтийг
            шууд уншдаг) тул болзолгүй зуравал хуудас ачаалахад шууд
            унана. `TopicPanel` ч мөн адил болзолтой зурдаг. */}
      {detail ? (
        <TopicDetailModal
          detail={detail}
          openContentDay={openDay}
          onClose={() => {
            setDetail(null);
            setOpenDay(null);
          }}
          onChanged={async () => {
            // Хуанли шинэ бичлэгийг ТЭР ДАРУЙ тусгах ёстой — эс бөгөөс
            // админ нэмсэн эсэхээ мэдэхгүй дахин нэмнэ.
            await load();
            if (detail?.id) setDetail(await knowledgeApi.topic(detail.id));
            onDataChanged?.();
          }}
        />
      ) : null}
    </>
  );
}

// ===========================================================================
//  Үндсэн хуудас
// ===========================================================================
const KnowledgeRegister = () => {
  const { user } = useUserContext();
  /**
   * Таб нь URL-аас эхэлж болно (`?tab=topics`).
   *
   * Лавлах хуудас (/knowledge/reference) дээрх группын СЭДВИЙН ТОО нь энэ
   * рүү заадаг болсон — «энэ группын сэдвүүдийг хараад засъя» гэсэн хүн
   * шууд «Мэдлэгийн сан» табд бууна. Эс бөгөөс «Өнөөдрийн мэдлэг» дээр
   * бууж, дараа нь табаа өөрөө хайх хэрэгтэй болно.
   *
   * ⚠ Зөвхөн МЭДЭГДЭЖ БУЙ түлхүүрийг хүлээж авна — URL-д санамсаргүй утга
   *   ирвэл antd таб олохгүй, хуудас ХООСОН харагдана.
   */
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    ["today", "topics", "weeks"].includes(urlTab) ? urlTab : "today",
  );
  // Админ талд хадгалсны дараа «Өнөөдрийн мэдлэг»-ийг дахин татахад ашиглана.
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = useCallback(() => setRefreshKey((n) => n + 1), []);

  const canManage = useMemo(() => {
    const roles = [
      ...(Array.isArray(user?.userroles) ? user.userroles : []),
      ...(Array.isArray(user?.roles) ? user.roles : []),
    ];
    const groups = String(user?.info?.usergroup || user?.usergroup || "")
      .split(",")
      .map((g) => g.trim())
      .filter(Boolean);
    return (
      MANAGE_ROLES.some((r) => roles.includes(r)) ||
      MANAGE_GROUPS.some((g) => groups.includes(g))
    );
  }, [user]);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ marginBottom: 14 }}>
        <h4 style={{ fontWeight: 800, color: PRIMARY, marginBottom: 2 }}>
          <i className="fas fa-graduation-cap" style={{ marginRight: 8 }} />
          Минутын мэдлэг
          <PageHelp style={{ marginLeft: 2 }} />
        </h4>
        <span style={{ color: "#8A94A6", fontSize: 12 }}>
          Долоо хоног бүр нэг сэдэв · өдөр бүр 1 минутын бичлэг, 1 асуулт ·
          биелэлт ХАБЭА хэлтэст хянагдана
        </span>
      </div>

      <Card bodyStyle={{ padding: 14 }}>
        {/* Таб солих бүрд «Өнөөдрийн мэдлэг»-ийг дахин татна — админ талд
            бичлэг/асуулт/хуваарь хадгалсан бол шууд тусгагдана. */}
        <Tabs
          activeKey={activeTab}
          onChange={(k) => {
            setActiveTab(k);
            if (k === "today") bumpRefresh();
          }}
          // ⚠ Нөхцөлт табыг `items` массивд `&&` эсвэл `? :`-ээр бичиж
          //   БОЛОХГҮЙ — false/null нь массивын элемент болж antd уначихна.
          //   Тиймээс spread-ээр огт оруулахгүй байх нь зөв.
          items={[
            {
              key: "today",
              label: "Өнөөдрийн мэдлэг",
              children: <TodayPanel refreshKey={refreshKey} />,
            },
            ...(canManage
              ? [
                  {
                    key: "topics",
                    label: "Мэдлэгийн сан",
                    children: <TopicPanel onDataChanged={bumpRefresh} />,
                  },
                  {
                    key: "weeks",
                    label: "7 хоногийн хуваарь",
                    children: <WeekPanel onDataChanged={bumpRefresh} />,
                  },
                ]
              : []),
          ]}
        />
      </Card>
    </div>
  );
};

export default KnowledgeRegister;
